import { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Add,
  Check,
  Close,
  LocalShipping,
  CheckCircle,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const STATUS_COLORS = {
  requested: 'warning',
  approved: 'info',
  in_transit: 'primary',
  completed: 'success',
  rejected: 'error',
  cancelled: 'default',
}

const columns = [
  { field: 'transfer_number', headerName: 'Transfer #', sortable: true, width: 130 },
  { field: 'asset_name', headerName: 'Asset', sortable: true },
  { field: 'asset_tag', headerName: 'Tag', sortable: true, width: 100 },
  {
    field: 'from_department_name',
    headerName: 'From',
    sortable: true,
    renderCell: (value, row) => value || row.from_location_name || '-',
  },
  {
    field: 'to_department_name',
    headerName: 'To',
    sortable: true,
    renderCell: (value, row) => value || row.to_location_name || '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.replace('_', ' ').toUpperCase()}
        color={STATUS_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  { field: 'requested_by_name', headerName: 'Requested By', sortable: true },
  {
    field: 'requested_date',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
]

const initialFormData = {
  asset: '',
  from_department: '',
  from_location: '',
  to_department: '',
  to_location: '',
  reason: '',
  notes: '',
}

const TransfersList = () => {
  const { hasPermission } = usePermissions()
  const [transfers, setTransfers] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedTransfer, setSelectedTransfer] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Options for dropdowns
  const [assets, setAssets] = useState([])
  const [departments, setDepartments] = useState([])
  const [locations, setLocations] = useState([])

  const fetchTransfers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/assets/transfers/')
      setTransfers(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching transfers:', error)
      toast.error('Failed to load transfers')
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [assetsRes, deptsRes, locsRes] = await Promise.all([
        api.get('/assets/assets/'),
        api.get('/common/departments/'),
        api.get('/assets/locations/'),
      ])
      setAssets(assetsRes.data.results || assetsRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setLocations(locsRes.data.results || locsRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  useEffect(() => {
    fetchTransfers()
    fetchOptions()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async () => {
    if (!formData.asset || !formData.to_department) {
      setErrors({
        asset: !formData.asset ? 'Asset is required' : '',
        to_department: !formData.to_department ? 'Destination is required' : '',
      })
      return
    }

    setFormLoading(true)
    try {
      await api.post('/assets/transfers/', {
        ...formData,
        from_department: formData.from_department || null,
        from_location: formData.from_location || null,
        to_location: formData.to_location || null,
      })
      toast.success('Transfer request submitted successfully')
      fetchTransfers()
      setFormOpen(false)
      setFormData(initialFormData)
    } catch (error) {
      console.error('Error creating transfer:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to create transfer request')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleAction = async (action, transferId) => {
    try {
      await api.post(`/assets/transfers/${transferId}/${action}/`)
      toast.success(`Transfer ${action.replace('_', ' ')} successful`)
      fetchTransfers()
      setDetailOpen(false)
    } catch (error) {
      console.error(`Error performing ${action}:`, error)
      toast.error(`Failed to ${action.replace('_', ' ')} transfer`)
    }
  }

  const handleRowClick = (row) => {
    setSelectedTransfer(row)
    setDetailOpen(true)
  }

  const getActionButtons = (transfer) => {
    if (!transfer) return null

    switch (transfer.status) {
      case 'requested':
        return hasPermission('transfers.approve') ? (
          <>
            <Button
              startIcon={<Check />}
              color="success"
              onClick={() => handleAction('approve', transfer.id)}
            >
              Approve
            </Button>
            <Button
              startIcon={<Close />}
              color="error"
              variant="outlined"
              onClick={() => handleAction('reject', transfer.id)}
            >
              Reject
            </Button>
          </>
        ) : null
      case 'approved':
        return hasPermission('transfers.execute') ? (
          <Button
            startIcon={<LocalShipping />}
            onClick={() => handleAction('start_transfer', transfer.id)}
          >
            Start Transfer
          </Button>
        ) : null
      case 'in_transit':
        return hasPermission('transfers.execute') ? (
          <Button
            startIcon={<CheckCircle />}
            color="success"
            onClick={() => handleAction('complete', transfer.id)}
          >
            Mark Completed
          </Button>
        ) : null
      default:
        return null
    }
  }

  return (
    <Box>
      <PageHeader
        title="Asset Transfers"
        subtitle="Track equipment movements between departments"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Transfers' },
        ]}
        actions={
          <PermissionGate permission="transfers.request">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              Request Transfer
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={transfers}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search transfers..."
          emptyMessage="No transfers found"
        />
      </Card>

      {/* Create Transfer Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Asset Transfer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.asset)} required>
                <InputLabel>Asset</InputLabel>
                <Select
                  name="asset"
                  value={formData.asset}
                  onChange={handleChange}
                  label="Asset"
                >
                  {assets.map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      {asset.asset_tag} - {asset.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>From Department</InputLabel>
                <Select
                  name="from_department"
                  value={formData.from_department}
                  onChange={handleChange}
                  label="From Department"
                >
                  <MenuItem value="">Current Location</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>From Location</InputLabel>
                <Select
                  name="from_location"
                  value={formData.from_location}
                  onChange={handleChange}
                  label="From Location"
                >
                  <MenuItem value="">None</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={Boolean(errors.to_department)} required>
                <InputLabel>To Department</InputLabel>
                <Select
                  name="to_department"
                  value={formData.to_department}
                  onChange={handleChange}
                  label="To Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>To Location</InputLabel>
                <Select
                  name="to_location"
                  value={formData.to_location}
                  onChange={handleChange}
                  label="To Location"
                >
                  <MenuItem value="">None</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Transfer"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                multiline
                rows={2}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Transfer {selectedTransfer?.transfer_number}
          <Chip
            label={selectedTransfer?.status?.replace('_', ' ').toUpperCase()}
            color={STATUS_COLORS[selectedTransfer?.status] || 'default'}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          {selectedTransfer && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <strong>Asset:</strong> {selectedTransfer.asset_name} ({selectedTransfer.asset_tag})
              </Grid>
              <Grid item xs={6}>
                <strong>From:</strong> {selectedTransfer.from_department_name || selectedTransfer.from_location_name || 'N/A'}
              </Grid>
              <Grid item xs={6}>
                <strong>To:</strong> {selectedTransfer.to_department_name || selectedTransfer.to_location_name || 'N/A'}
              </Grid>
              <Grid item xs={12}>
                <strong>Reason:</strong> {selectedTransfer.reason || 'N/A'}
              </Grid>
              <Grid item xs={6}>
                <strong>Requested By:</strong> {selectedTransfer.requested_by_name}
              </Grid>
              <Grid item xs={6}>
                <strong>Requested Date:</strong> {selectedTransfer.requested_date ? new Date(selectedTransfer.requested_date).toLocaleDateString() : 'N/A'}
              </Grid>
              {selectedTransfer.approved_by_name && (
                <>
                  <Grid item xs={6}>
                    <strong>Approved By:</strong> {selectedTransfer.approved_by_name}
                  </Grid>
                  <Grid item xs={6}>
                    <strong>Approved Date:</strong> {selectedTransfer.approved_date ? new Date(selectedTransfer.approved_date).toLocaleDateString() : 'N/A'}
                  </Grid>
                </>
              )}
              {selectedTransfer.notes && (
                <Grid item xs={12}>
                  <strong>Notes:</strong> {selectedTransfer.notes}
                </Grid>
              )}
              {selectedTransfer.rejection_reason && (
                <Grid item xs={12}>
                  <strong>Rejection Reason:</strong> {selectedTransfer.rejection_reason}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {getActionButtons(selectedTransfer)}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TransfersList
